import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { StudentSectionLayout } from './StudentSectionLayout';
import { SupportShellEmbeddedProvider } from '../modules/support/components/SupportPageShell';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ProtectedRoute } from './ProtectedRoute';
import { ROUTES, USER_ROLES } from '../utils/constants';
import { AUDIT_ROUTE_ROLES } from '../utils/auditAccess';
import { getDefaultRouteForRoles } from '../utils/postLoginRedirect';
import { SchoolLanguageProvider } from '../contexts/SchoolLanguageContext';
import { DevForceEnglish } from '../components/layout/DevForceEnglish';

const RouteFallback = () => (
  <div className="flex min-h-[40vh] items-center justify-center" aria-busy="true">
    <Loader2 className="h-8 w-8 animate-spin text-app-primary" aria-hidden />
  </div>
);

const lazyNamed = <T extends Record<string, React.ComponentType>>(loader: () => Promise<T>, name: keyof T) =>
  lazy(() => loader().then((m) => ({ default: m[name] })));

/** Legacy email links used /auth/invites/verify; preserve query string on redirect. */
const LegacyInviteVerifyRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={`${ROUTES.INVITE_VERIFY}${location.search}`} replace />;
};

// Auth Pages (eager — first paint)
import { LoginPage } from '../pages/auth/LoginPage';
import { RegisterPage } from '../pages/auth/RegisterPage';
import { ForgotPasswordPage } from '../pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '../pages/auth/ResetPasswordPage';
import { InviteVerifyPage } from '../pages/auth/InviteVerifyPage';
import { InviteAcceptPage } from '../pages/auth/InviteAcceptPage';
import { LmsLaunchPage } from '../pages/lms/LmsLaunchPage';
import { AuthenticatePage } from '../pages/auth/AuthenticatePage';
import { BanyanProEmbedPage } from '../integrations/banyanpro/embed/BanyanProEmbedPage';
import { LandingPage } from '../pages/LandingPage';
import { MalayLandingPage } from '../pages/landing/MalayLandingPage';
import { PublicLandingResolver } from '../components/landing/PublicLandingResolver';
import { ModulesPage } from '../pages/landing/ModulesPage';
import { TeacherLandingPage } from '../pages/landing/TeacherLandingPage';
import { PrivacyPage } from '../pages/landing/PrivacyPage';
import PricingPage from '../pages/landing/PricingPage';
import ContactPage from '../pages/landing/ContactPage';
import TermsPage from '../pages/landing/TermsPage';
import Grade3AssignmentPage from '../pages/Grade3AssignmentPage';

import { StudentDashboard } from '../pages/student/Dashboard';
import MalayLearningProgressPage from '../pages/student/MalayLearningProgress';
import { StudentSchoolFeatureGate } from '../components/guards/StudentSchoolFeatureGate';
import { ReadingComprehensionAccessGate } from '../components/guards/ReadingComprehensionAccessGate';
import { StudentActivityGradeGate } from '../components/guards/StudentActivityGradeGate';

// Dev team (lazy)
const DevDashboard = lazyNamed(() => import('../pages/devteam/Dashboard'), 'DevDashboard');
const SchoolsList = lazyNamed(() => import('../pages/devteam/SchoolsList'), 'SchoolsList');
const SchoolOnboardingWizard = lazy(() => import('../pages/devteam/SchoolOnboardingWizard'));
const SchoolCreatePage = lazyNamed(() => import('../pages/devteam/SchoolCreatePage'), 'SchoolCreatePage');
const SchoolEditPage = lazyNamed(() => import('../pages/devteam/SchoolEditPage'), 'SchoolEditPage');
const SchoolDetails = lazyNamed(() => import('../pages/devteam/SchoolDetails'), 'SchoolDetails');
const AuditLogs = lazyNamed(() => import('../pages/devteam/AuditLogs'), 'AuditLogs');
const InviteDevTeam = lazyNamed(() => import('../pages/devteam/InviteDevTeam'), 'InviteDevTeam');
const PromptTemplatePage = lazyNamed(() => import('../pages/devteam/PromptTemplatePage'), 'PromptTemplatePage');
const AuditConsoleLayout = lazyNamed(() => import('../pages/devteam/audit-console/AuditConsoleLayout'), 'AuditConsoleLayout');
const AuditOverviewPage = lazyNamed(() => import('../pages/devteam/audit-console/OverviewPage'), 'OverviewPage');
const LiveMonitorPage = lazyNamed(() => import('../pages/devteam/audit-console/LiveMonitorPage'), 'LiveMonitorPage');
const UserActivityPage = lazyNamed(() => import('../pages/devteam/audit-console/UserActivityPage'), 'UserActivityPage');
const ApiLogsPage = lazyNamed(() => import('../pages/devteam/audit-console/ApiLogsPage'), 'ApiLogsPage');
const AiUsagePage = lazyNamed(() => import('../pages/devteam/audit-console/AiUsagePage'), 'AiUsagePage');
const SuspiciousPage = lazyNamed(() => import('../pages/devteam/audit-console/SuspiciousPage'), 'SuspiciousPage');
const AuthSessionsPage = lazyNamed(() => import('../pages/devteam/audit-console/AuthSessionsPage'), 'AuthSessionsPage');
const TraceDetailPage = lazyNamed(() => import('../pages/devteam/audit-console/TraceDetailPage'), 'TraceDetailPage');
const DevTutorQuotasPage = lazy(() => import('../pages/devteam/DevTutorQuotasPage'));
const StudentTranscriptsListPage = lazy(() => import('../pages/devteam/StudentTranscriptsListPage'));
const StudentTranscriptDetailPage = lazy(() => import('../pages/devteam/StudentTranscriptDetailPage'));
const DevPlanManagementPage = lazy(() => import('../pages/devteam/DevPlanManagementPage'));
const DevPlanEditorPage = lazy(() => import('../pages/devteam/DevPlanEditorPage'));
const DevSubscriptionsPage = lazy(() => import('../pages/devteam/DevSubscriptionsPage'));
const DevWebhooksPage = lazy(() => import('../pages/devteam/DevWebhooksPage'));
const DevBillingAnalyticsPage = lazy(() => import('../pages/devteam/DevBillingAnalyticsPage'));
const DevSubscriptionAlertsPage = lazy(() => import('../pages/devteam/DevSubscriptionAlertsPage'));
const DevBusinessPage = lazy(() => import('../pages/devteam/DevBusinessPage'));
const DevAlertsPage = lazy(() => import('../pages/devteam/DevAlertsPage'));
const DevCompliancePage = lazy(() => import('../pages/devteam/DevCompliancePage'));
const DevIbookListPage = lazy(() => import('../modules/ibook/authoring/DevIbookListPage'));
const DevIbookNewPage = lazy(() => import('../modules/ibook/authoring/DevIbookNewPage'));
const DevIbookEditPage = lazy(() => import('../modules/ibook/authoring/DevIbookEditPage'));
const DevIbookPreviewPage = lazy(() => import('../modules/ibook/authoring/DevIbookPreviewPage'));
const DevIbookPublishPage = lazy(() => import('../modules/ibook/authoring/DevIbookPublishPage'));
const TenantIbooksPage = lazy(() => import('../modules/ibook/authoring/TenantIbooksPage'));
const StudentIbookListPage = lazy(() => import('../modules/ibook/reader/StudentIbookListPage'));
const StudentIbookReaderPage = lazy(() => import('../modules/ibook/reader/StudentIbookReaderPage'));
const StudentInteractiveBookReaderPage = lazy(
  () => import('../modules/interactive-book/reader/StudentInteractiveBookReaderPage'),
);
const DevPlatformAnalyticsPage = lazy(() => import('../pages/dev/analytics/DevPlatformAnalyticsPage'));
const DevStudentUsagePage = lazy(() => import('../pages/dev/analytics/DevStudentUsagePage'));
const DevStudentDetailPage = lazy(() => import('../pages/dev/analytics/DevStudentDetailPage'));

// Admin (lazy)
const AdminDashboard = lazyNamed(() => import('../pages/admin/Dashboard'), 'AdminDashboard');
const SchoolProfile = lazyNamed(() => import('../pages/admin/SchoolProfile'), 'SchoolProfile');
const SchoolSetup = lazyNamed(() => import('../pages/admin/SchoolSetup'), 'SchoolSetup');
const ClassesList = lazyNamed(() => import('../pages/admin/ClassesList'), 'ClassesList');
const StudentsList = lazyNamed(() => import('../pages/admin/StudentsList'), 'StudentsList');
const TeachersList = lazyNamed(() => import('../pages/admin/TeachersList'), 'TeachersList');
const TeacherDetails = lazy(() => import('../pages/admin/TeacherDetails'));
const ParentsList = lazyNamed(() => import('../pages/admin/ParentsList'), 'ParentsList');
const AdminSchoolSubscriptionPage = lazy(() => import('../pages/admin/AdminSchoolSubscriptionPage'));
const AdminBillingPage = lazy(() => import('../pages/admin/AdminBillingPage'));
const AdminBillingInvoicesPage = lazy(() => import('../pages/admin/AdminBillingInvoicesPage'));
const AdminBillingHistoryPage = lazy(() => import('../pages/admin/AdminBillingHistoryPage'));
const TutorQuotasAdminPage = lazy(() => import('../pages/admin/TutorQuotasAdminPage'));
const AdminQuotaDashboardPage = lazy(() => import('../pages/admin/AdminQuotaDashboardPage'));
const AdminQuotaSchoolDetailPage = lazy(() => import('../pages/admin/AdminQuotaSchoolDetailPage'));
const AdminAnalyticsPage = lazy(() => import('../pages/admin/analytics/AdminAnalyticsPage'));
const AdminStudentUsagePage = lazy(() => import('../pages/admin/analytics/AdminStudentUsagePage'));
const AdminStudentDetailPage = lazy(() => import('../pages/admin/analytics/AdminStudentDetailPage'));
const AdminActivityLogsPage = lazy(() => import('../pages/admin/activity/AdminActivityLogsPage'));
const WeeklyReportsSettings = lazyNamed(() => import('../pages/admin/WeeklyReportsSettings'), 'WeeklyReportsSettings');
const TutorThemeAdminPage = lazy(() => import('../pages/admin/TutorThemeAdminPage'));
const CreateStudent = lazyNamed(() => import('../pages/admin/CreateStudent'), 'CreateStudent');
const StudentViewPage = lazyNamed(() => import('../pages/admin/StudentViewPage'), 'StudentViewPage');
const SectionsPage = lazyNamed(() => import('../pages/admin/SectionsPage'), 'SectionsPage');
const InviteTeacher = lazyNamed(() => import('../pages/admin/InviteTeacher'), 'InviteTeacher');
const InviteParent = lazyNamed(() => import('../pages/admin/InviteParent'), 'InviteParent');
const InviteStudent = lazyNamed(() => import('../pages/admin/InviteStudent'), 'InviteStudent');
const AdminRevisionVocabularyOverviewPage = lazy(() => import('../pages/admin/content/AdminRevisionVocabularyOverviewPage'));
const EmailProviderConfigsPage = lazyNamed(() => import('../pages/admin/comms/EmailProviderConfigsPage'), 'EmailProviderConfigsPage');
const EmailTemplatesPage = lazyNamed(() => import('../pages/admin/comms/EmailTemplatesPage'), 'EmailTemplatesPage');
const EmailTemplateBuilderPage = lazyNamed(() => import('../pages/admin/comms/EmailTemplateBuilderPage'), 'EmailTemplateBuilderPage');
const SupportRoutes = lazy(() => import('../pages/support/SupportRoutes'));

// Teacher (lazy)
const TeacherDashboard = lazyNamed(() => import('../pages/teacher/Dashboard'), 'TeacherDashboard');
const TeacherProfile = lazyNamed(() => import('../pages/teacher/Profile'), 'TeacherProfile');
const MySectionsPage = lazyNamed(() => import('../pages/teacher/MySectionsPage'), 'MySectionsPage');
const AssignmentsPage = lazyNamed(() => import('../pages/teacher/Assignments'), 'AssignmentsPage');
const ReviewSubmissionPage = lazyNamed(() => import('../pages/teacher/ReviewSubmissionPage'), 'ReviewSubmissionPage');
const AnalyticsPage = lazyNamed(() => import('../pages/teacher/Analytics'), 'AnalyticsPage');
const TeacherTutorAnalyticsPage = lazyNamed(() => import('../pages/teacher/TeacherTutorAnalytics'), 'TeacherTutorAnalyticsPage');
const TeacherStudentAnalyticsPage = lazyNamed(() => import('../pages/teacher/TeacherStudentAnalytics'), 'TeacherStudentAnalyticsPage');
const LessonCanDoPage = lazy(() => import('../pages/teacher/LessonCanDo'));
const TeacherStudentProgressRedirectPage = lazy(
  () => import('../pages/teacher/TeacherStudentProgressRedirect'),
);
const TeacherClassProgressPage = lazy(() => import('../pages/teacher/TeacherClassProgress'));
const CommunicationAnalyticsPage = lazy(() => import('../pages/teacher/CommunicationAnalytics'));
const CommunicationAlertsPage = lazy(() => import('../pages/teacher/CommunicationAlerts'));
const TeacherScenarioCanDoPage = lazy(() => import('../pages/teacher/TeacherScenarioCanDo'));
const ScenarioAuthoringPage = lazy(() => import('../pages/teacher/ScenarioAuthoring'));
const ScenarioAuthoringFormPage = lazy(() => import('../pages/teacher/ScenarioAuthoringForm'));
const SettingsPage = lazyNamed(() => import('../pages/teacher/Settings'), 'SettingsPage');
const ContentManagementLayout = lazy(() => import('../pages/teacher/content/ContentManagementLayout'));
const ContentManagementRedirect = lazy(() =>
  import('../pages/teacher/content/ContentManagementLayout').then((m) => ({ default: m.ContentManagementRedirect }))
);
const ContentLessonsPage = lazy(() => import('../pages/teacher/content/ContentLessonsPage'));
const ContentStoriesPage = lazy(() => import('../pages/teacher/content/ContentStoriesPage'));
const ContentRhymesPage = lazy(() => import('../pages/teacher/content/ContentRhymesPage'));
const ContentVocabularyPage = lazy(() => import('../pages/teacher/content/ContentVocabularyPage'));
const ContentRevisionVocabularyPage = lazy(() => import('../pages/teacher/content/ContentRevisionVocabularyPage'));

// Student pages except dashboard (lazy)
const StudentProfile = lazyNamed(() => import('../pages/student/Profile'), 'StudentProfile');
const StudentActivityPage = lazyNamed(() => import('../pages/student/StudentActivityPage'), 'StudentActivityPage');
const ConversationPage = lazyNamed(() => import('../pages/student/Conversation'), 'ConversationPage');
const TamilTutorPage = lazyNamed(() => import('../pages/student/TamilTutor'), 'TamilTutorPage');
const GamesPage = lazyNamed(() => import('../pages/student/Games'), 'GamesPage');
const TamilPhonicsFlashcards = lazy(() => import('../pages/student/PhonicsLab'));
const StoryStudioPage = lazyNamed(() => import('../pages/student/StoryStudio'), 'StoryStudioPage');
const VocabularyHubPage = lazy(() => import('../pages/student/VocabularyHubPage'));
const VocabularyLessonPage = lazy(() => import('../pages/student/VocabularyLessonPage'));
const VocabularyDictionaryPage = lazy(() => import('../pages/student/VocabularyDictionaryPage'));
const VocabularyFlashcardsPage = lazy(() => import('../pages/student/VocabularyFlashcardsPage'));
const RevisionFlashcardLessonPage = lazy(() => import('../pages/student/RevisionFlashcardLessonPage'));
const AITutorAvatarPage = lazyNamed(() => import('../pages/student/AITutorAvatar'), 'AITutorAvatarPage');
const PictureTalkListPage = lazy(() => import('../pages/student/PictureTalkListPage'));
const PictureTalkScenePage = lazy(() => import('../pages/student/PictureTalkScenePage'));
const WritingPage = lazyNamed(() => import('../pages/student/Writing'), 'WritingPage');
const WritingHistoryPage = lazyNamed(() => import('../pages/student/WritingHistoryPage'), 'WritingHistoryPage');
const LibraryPage = lazyNamed(() => import('../pages/student/Library'), 'LibraryPage');
const ProgressPage = lazyNamed(() => import('../pages/student/Progress'), 'ProgressPage');
const StudentSettingsPage = lazyNamed(() => import('../pages/student/StudentSettings'), 'StudentSettingsPage');
const StudentAssignmentsPage = lazyNamed(() => import('../pages/student/StudentAssignments'), 'StudentAssignmentsPage');
const StudentCourses = lazyNamed(() => import('../pages/student/StudentCourses'), 'StudentCourses');
const GradePartsList = lazyNamed(() => import('../pages/student/GradePartsList'), 'GradePartsList');
const StudentCoursePlayer = lazyNamed(() => import('../pages/student/StudentCoursePlayer'), 'StudentCoursePlayer');
const AiTutorLivePage = lazy(() => import('../pages/student/AiTutorLive'));
const CommunicationTutorPage = lazy(() => import('../pages/student/CommunicationTutor'));
const NarrativeWritingLandingPage = lazy(() => import('../pages/student/NarrativeWritingLanding'));
const NarrativeWritingTutorPage = lazy(() => import('../pages/student/NarrativeWritingTutor'));
const NarrativeWritingWritePage = lazy(() => import('../pages/student/NarrativeWritingWrite'));
const ExpositoryWritingStudioPage = lazy(() => import('../pages/student/ExpositoryWritingStudio'));
const TamilCompositionWritingStudioPage = lazy(
  () => import('../pages/student/TamilCompositionWritingStudio'),
);
const ReadingComprehensionPage = lazy(() => import('../pages/student/ReadingComprehension'));
const StructuredReadingDemoPage = lazy(() => import('../pages/student/StructuredReadingDemoPage'));
const StructuredPageComparePage = lazy(() => import('../pages/dev/StructuredPageComparePage'));
const AuthoringStudioHomePage = lazy(() => import('../pages/dev/authoring/AuthoringStudioHomePage'));
const AuthoringBooksPage = lazy(() => import('../pages/dev/authoring/AuthoringBooksPage'));
const AuthoringBookPagesPage = lazy(() => import('../pages/dev/authoring/AuthoringBookPagesPage'));
const AuthoringSourcePageDetailPage = lazy(() => import('../pages/dev/authoring/AuthoringSourcePageDetailPage'));
const AuthoringWorkspacePage = lazy(() => import('../pages/dev/authoring/AuthoringWorkspacePage'));
const AuthoringPreviewPage = lazy(() => import('../pages/dev/authoring/AuthoringPreviewPage'));
const AuthoringMigrationReviewPage = lazy(() => import('../pages/dev/authoring/AuthoringMigrationReviewPage'));
const InteractiveBookPreviewPage = lazy(() => import('../pages/dev/InteractiveBookPreviewPage'));
const InteractiveBookVersionPreviewPage = lazy(() => import('../pages/dev/InteractiveBookVersionPreviewPage'));
const InteractiveBookComposerPage = lazy(() => import('../pages/dev/InteractiveBookComposerPage'));
const InteractiveBooksPage = lazy(() => import('../pages/dev/InteractiveBooksPage'));
const KeeladiStructuredBookReaderPage = lazy(() => import('../pages/student/KeeladiStructuredBookReaderPage'));
const ScenarioConversationPage = lazy(() => import('../pages/student/ScenarioConversation'));
const ScenarioClassroomPage = lazy(() => import('../pages/student/ScenarioClassroom'));
const TutorAnalyticsPage = lazyNamed(() => import('../pages/student/TutorAnalytics'), 'TutorAnalyticsPage');
const StudentAnalyticsPage = lazyNamed(() => import('../pages/student/Analytics'), 'StudentAnalyticsPage');
const StudentAnalyticsChartsPage = lazyNamed(() => import('../pages/student/AnalyticsCharts'), 'StudentAnalyticsChartsPage');

// Parent (lazy)
const ParentDashboard = lazyNamed(() => import('../pages/parent/Dashboard'), 'ParentDashboard');
const LinkChild = lazyNamed(() => import('../pages/parent/LinkChild'), 'LinkChild');
const ChildrenList = lazyNamed(() => import('../pages/parent/ChildrenList'), 'ChildrenList');
const ParentCommunicationProgressPage = lazy(() => import('../pages/parent/ParentCommunicationProgress'));

// Onboarding + profile (lazy)
const TeacherProfilePage = lazyNamed(() => import('../pages/onboarding/TeacherProfilePage'), 'TeacherProfilePage');
const StudentProfilePage = lazyNamed(() => import('../pages/onboarding/StudentProfilePage'), 'StudentProfilePage');
const ProfilePage = lazyNamed(() => import('../pages/ProfilePage'), 'ProfilePage');
const DevCouponsPage = lazyNamed(() => import('../pages/profile/DevCouponsPage'), 'DevCouponsPage');

export const AppRoutes: React.FC = () => {
  const { isAuthenticated, user } = useAuth();

  const getDefaultRoute = () => {
    if (!user) return ROUTES.LOGIN;
    return getDefaultRouteForRoles(user.roles, user.onboarding_complete);
  };

  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public Routes */}
      <Route
        path={ROUTES.LANDING}
        element={isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <PublicLandingResolver />}
      />
      <Route
        path={ROUTES.LANDING_MS}
        element={isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <MalayLandingPage />}
      />
      <Route
        path={ROUTES.LANDING_MALAY}
        element={isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <MalayLandingPage />}
      />
      <Route
        path={ROUTES.LOGIN}
        element={isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <LoginPage />}
      />
      <Route
        path={ROUTES.REGISTER}
        element={isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <RegisterPage />}
      />
      <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
      <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
      <Route path="/auth/invites/verify" element={<LegacyInviteVerifyRedirect />} />
      <Route path={ROUTES.INVITE_VERIFY} element={<InviteVerifyPage />} />
      <Route path={ROUTES.INVITE_ACCEPT} element={<InviteAcceptPage />} />
      <Route path={ROUTES.LMS_LAUNCH} element={<LmsLaunchPage />} />
      <Route path={ROUTES.AUTHENTICATE} element={<AuthenticatePage />} />
      <Route path={ROUTES.EMBED_BANYANPRO} element={<BanyanProEmbedPage />} />
      <Route path={ROUTES.MODULES} element={<ModulesPage />} />
      <Route path={ROUTES.TEACHER_LANDING} element={<TeacherLandingPage />} />
      <Route path={ROUTES.PRIVACY} element={<PrivacyPage />} />
      <Route path={ROUTES.PRICING} element={<PricingPage />} />
      <Route path={ROUTES.CONTACT} element={<ContactPage />} />
      <Route path={ROUTES.TERMS} element={<TermsPage />} />
      <Route path={ROUTES.GRADE3_ASSIGNMENTS_DEMO} element={<Grade3AssignmentPage />} />

      {/* Profile (header); Coupons only for DEV_TEAM */}
      <Route
        path={ROUTES.PROFILE_COUPONS}
        element={
          <ProtectedRoute requiredRole={USER_ROLES.DEV_TEAM}>
            <DevCouponsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PROFILE}
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Dev Team Routes */}
      <Route
        path={`${ROUTES.DEV_AUDIT_CONSOLE}/*`}
        element={
          <ProtectedRoute requiredRole={AUDIT_ROUTE_ROLES}>
            <Routes>
              <Route element={<AuditConsoleLayout />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<AuditOverviewPage />} />
                <Route path="live" element={<LiveMonitorPage />} />
                <Route path="users" element={<UserActivityPage />} />
                <Route path="api-logs" element={<ApiLogsPage />} />
                <Route path="ai-usage" element={<AiUsagePage />} />
                <Route path="suspicious" element={<SuspiciousPage />} />
                <Route path="auth-sessions" element={<AuthSessionsPage />} />
                <Route path="trace/:traceId" element={<TraceDetailPage />} />
              </Route>
            </Routes>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dev/*"
        element={
          <DevForceEnglish>
          <ProtectedRoute requiredRole={USER_ROLES.DEV_TEAM}>
            <Routes>
              <Route path="dashboard" element={<DevDashboard />} />
              <Route path="schools" element={<SchoolsList />} />
              <Route path="schools/create" element={<SchoolCreatePage />} />
              <Route path="schools/onboard" element={<SchoolOnboardingWizard />} />
              <Route path="schools/new" element={<Navigate to={ROUTES.DEV_SCHOOLS_CREATE} replace />} />
              <Route path="schools/:id/edit" element={<SchoolEditPage />} />
              <Route path="schools/:id" element={<SchoolDetails />} />
              <Route path="comms/email-configs" element={<EmailProviderConfigsPage />} />
              <Route path="comms/email-templates" element={<EmailTemplatesPage />} />
              <Route path="comms/email-templates/new" element={<EmailTemplateBuilderPage />} />
              <Route path="weekly-reports" element={<WeeklyReportsSettings />} />
              <Route path="audit-logs" element={<AuditLogs />} />
              <Route path="analytics/students/:studentId" element={<DevStudentDetailPage />} />
              <Route path="analytics/students" element={<DevStudentUsagePage />} />
              <Route path="analytics" element={<DevPlatformAnalyticsPage />} />
              <Route path="plans" element={<DevPlanManagementPage />} />
              <Route path="plans/new" element={<DevPlanEditorPage />} />
              <Route path="plans/:planId" element={<DevPlanEditorPage />} />
              <Route path="webhooks" element={<DevWebhooksPage />} />
              <Route path="billing-analytics" element={<DevBillingAnalyticsPage />} />
              <Route path="prompt-templates" element={<PromptTemplatePage />} />
              <Route path="tutor-quotas" element={<DevTutorQuotasPage />} />
              <Route path="student-transcripts" element={<StudentTranscriptsListPage />} />
              <Route
                path="student-transcripts/:transcriptSessionId"
                element={<StudentTranscriptDetailPage />}
              />
              <Route path="subscriptions" element={<DevSubscriptionsPage />} />
              <Route path="subscription-alerts" element={<DevSubscriptionAlertsPage />} />
              <Route path="alerts" element={<DevAlertsPage />} />
              <Route path="compliance" element={<DevCompliancePage />} />
              <Route path="business" element={<DevBusinessPage />} />
              <Route path="invite" element={<InviteDevTeam />} />
              <Route
                path="digital-page-compare"
                element={<Navigate to={ROUTES.DEV_DIGITAL_PAGE_COMPARE_JENDELA_P39} replace />}
              />
              <Route path="digital-page-compare/jendela-p39" element={<StructuredPageComparePage />} />
              <Route path="digital-page-compare/*" element={<StructuredPageComparePage />} />
              <Route path="structured-textbook/migration" element={<AuthoringMigrationReviewPage />} />
              <Route path="structured-textbook/books" element={<AuthoringBooksPage />} />
              <Route path="structured-textbook/books/:bookId/pages/:pageId/preview" element={<AuthoringPreviewPage />} />
              <Route path="structured-textbook/books/:bookId/pages/:pageId/edit" element={<AuthoringWorkspacePage />} />
              <Route path="structured-textbook/books/:bookId/source/:sourcePageId" element={<AuthoringSourcePageDetailPage />} />
              <Route path="structured-textbook/books/:bookId" element={<AuthoringBookPagesPage />} />
              <Route path="structured-textbook" element={<AuthoringStudioHomePage />} />
              <Route path="interactive-books/:bookId/versions/:revision/preview" element={<InteractiveBookVersionPreviewPage />} />
              <Route path="interactive-books/:bookId/preview/:pageIndex" element={<InteractiveBookPreviewPage />} />
              <Route path="interactive-books/:bookId/preview" element={<InteractiveBookPreviewPage />} />
              <Route path="interactive-books/:bookId/composer" element={<InteractiveBookComposerPage />} />
              <Route path="interactive-books" element={<InteractiveBooksPage />} />
              <Route path="bookflow" element={<Navigate to={ROUTES.DEV_IBOOK_LIST} replace />} />
              <Route path="bookflow/*" element={<Navigate to={ROUTES.DEV_IBOOK_LIST} replace />} />
              <Route path="ibook" element={<Suspense fallback={<RouteFallback />}><DevIbookListPage /></Suspense>} />
              <Route path="ibook/new" element={<Suspense fallback={<RouteFallback />}><DevIbookNewPage /></Suspense>} />
              <Route path="ibook/:bookId/edit" element={<Suspense fallback={<RouteFallback />}><DevIbookEditPage /></Suspense>} />
              <Route path="ibook/:bookId/preview" element={<Suspense fallback={<RouteFallback />}><DevIbookPreviewPage /></Suspense>} />
              <Route path="ibook/:bookId/publish" element={<Suspense fallback={<RouteFallback />}><DevIbookPublishPage /></Suspense>} />
              <Route path="schools/:tenantId/ibooks" element={<Suspense fallback={<RouteFallback />}><TenantIbooksPage /></Suspense>} />
              <Route path="support/*" element={<SupportRoutes />} />
              <Route path="*" element={<Navigate to={ROUTES.DEV_DASHBOARD} replace />} />
            </Routes>
          </ProtectedRoute>
          </DevForceEnglish>
        }
      />

      {/* Quota operations dashboard (school admin + platform admin) */}
      <Route
        path="/admin/quota-dashboard"
        element={
          <SchoolLanguageProvider>
          <ProtectedRoute requiredRole={[USER_ROLES.SCHOOL_ADMIN, USER_ROLES.DEV_TEAM]}>
            <Suspense fallback={<RouteFallback />}>
              <AdminQuotaDashboardPage />
            </Suspense>
          </ProtectedRoute>
          </SchoolLanguageProvider>
        }
      />
      <Route
        path="/admin/quota-dashboard/:schoolId"
        element={
          <SchoolLanguageProvider>
          <ProtectedRoute requiredRole={[USER_ROLES.SCHOOL_ADMIN, USER_ROLES.DEV_TEAM]}>
            <Suspense fallback={<RouteFallback />}>
              <AdminQuotaSchoolDetailPage />
            </Suspense>
          </ProtectedRoute>
          </SchoolLanguageProvider>
        }
      />

      {/* Tutor theme management (school admin + platform admin) */}
      <Route
        path={ROUTES.ADMIN_TUTOR_THEME}
        element={
          <ProtectedRoute requiredRole={[USER_ROLES.SCHOOL_ADMIN, USER_ROLES.DEV_TEAM]}>
            <Suspense fallback={<RouteFallback />}>
              <TutorThemeAdminPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* Support portal (school admin + support agent) */}
      <Route
        path="/admin/support/*"
        element={
          <SchoolLanguageProvider>
          <ProtectedRoute requiredRole={[USER_ROLES.SCHOOL_ADMIN, USER_ROLES.SUPPORT_AGENT]}>
            <Suspense fallback={<RouteFallback />}>
              <SupportRoutes />
            </Suspense>
          </ProtectedRoute>
          </SchoolLanguageProvider>
        }
      />

      {/* School Admin Routes */}
      <Route
        path="/admin/*"
        element={
          <SchoolLanguageProvider>
          <ProtectedRoute requiredRole={USER_ROLES.SCHOOL_ADMIN} requireOnboarding>
            <Routes>
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="school" element={<SchoolProfile />} />
              <Route path="setup" element={<SchoolSetup />} />
              <Route path="classes" element={<ClassesList />} />
              <Route path="sections" element={<SectionsPage />} />
              <Route path="students" element={<StudentsList />} />
              <Route path="teachers" element={<TeachersList />} />
              <Route path="teachers/:teacherId" element={<TeacherDetails />} />
              <Route path="parents" element={<ParentsList />} />
              <Route path="billing" element={<AdminBillingPage />} />
              <Route path="billing/invoices" element={<AdminBillingInvoicesPage />} />
              <Route path="plans" element={<Navigate to={ROUTES.ADMIN_SUBSCRIPTION} replace />} />
              <Route path="billing-history" element={<AdminBillingHistoryPage />} />
              <Route path="subscription" element={<AdminSchoolSubscriptionPage />} />
              <Route path="tutor-quotas" element={<TutorQuotasAdminPage />} />
              <Route path="analytics/students/:studentId" element={<AdminStudentDetailPage />} />
              <Route path="analytics/students" element={<AdminStudentUsagePage />} />
              <Route path="analytics" element={<AdminAnalyticsPage />} />
              <Route path="activity-logs" element={<AdminActivityLogsPage />} />
              <Route path="students/new" element={<CreateStudent />} />
              <Route path="students/:id" element={<StudentViewPage />} />
              <Route path="invites/teacher" element={<InviteTeacher />} />
              <Route path="invites/parent" element={<InviteParent />} />
              <Route path="invites/student" element={<InviteStudent />} />
              <Route path="scenario-authoring" element={<ScenarioAuthoringPage />} />
              <Route path="scenario-authoring/new" element={<ScenarioAuthoringFormPage />} />
              <Route path="scenario-authoring/:scenarioId" element={<ScenarioAuthoringFormPage />} />
              <Route path="content/revision-vocabulary" element={<AdminRevisionVocabularyOverviewPage />} />
              <Route path="comms/email-configs" element={<EmailProviderConfigsPage />} />
              <Route path="comms/email-templates" element={<EmailTemplatesPage />} />
              <Route path="comms/email-templates/new" element={<EmailTemplateBuilderPage />} />
              <Route path="weekly-reports" element={<WeeklyReportsSettings />} />
              <Route path="*" element={<Navigate to={ROUTES.ADMIN_DASHBOARD} replace />} />
            </Routes>
          </ProtectedRoute>
          </SchoolLanguageProvider>
        }
      />

      {/* Teacher Routes */}
      <Route
        path="/teacher/*"
        element={
          <SchoolLanguageProvider>
          <ProtectedRoute requiredRole={USER_ROLES.TEACHER} requireOnboarding>
            <Routes>
              <Route path="dashboard" element={<TeacherDashboard />} />
              <Route path="profile" element={<TeacherProfile />} />
              <Route path="assignments" element={<AssignmentsPage />} />
              <Route path="submissions/:submissionId/review" element={<ReviewSubmissionPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="tutor-analytics" element={<TeacherTutorAnalyticsPage />} />
              <Route path="tutor-analytics/student/:studentId" element={<TeacherStudentAnalyticsPage />} />
              <Route path="can-do" element={<LessonCanDoPage />} />
              <Route
                path="communication-assignments"
                element={<Navigate to={ROUTES.TEACHER_ASSIGNMENTS_COMM_TASKS} replace />}
              />
              <Route path="communication-analytics" element={<CommunicationAnalyticsPage />} />
              <Route path="communication-alerts" element={<CommunicationAlertsPage />} />
              <Route path="scenario-can-do" element={<TeacherScenarioCanDoPage />} />
              <Route path="scenario-authoring" element={<ScenarioAuthoringPage />} />
              <Route path="scenario-authoring/new" element={<ScenarioAuthoringFormPage />} />
              <Route path="scenario-authoring/:scenarioId" element={<ScenarioAuthoringFormPage />} />
              <Route path="class-progress" element={<TeacherClassProgressPage />} />
              <Route path="class_progress" element={<TeacherStudentProgressRedirectPage />} />
              <Route path="student-progress" element={<TeacherStudentProgressRedirectPage />} />
              <Route path="student_progress" element={<TeacherStudentProgressRedirectPage />} />
              <Route path="content" element={<ContentManagementLayout />}>
                <Route index element={<ContentManagementRedirect />} />
                <Route path="lessons" element={<ContentLessonsPage />} />
                <Route path="stories" element={<ContentStoriesPage />} />
                <Route path="rhymes" element={<ContentRhymesPage />} />
                <Route path="vocabulary" element={<ContentVocabularyPage />} />
                <Route path="revision-vocabulary" element={<ContentRevisionVocabularyPage />} />
              </Route>
              <Route path="settings" element={<SettingsPage />} />
              <Route path="my-sections" element={<MySectionsPage />} />
              <Route path="support/*" element={<SupportRoutes />} />
              <Route path="*" element={<Navigate to={ROUTES.TEACHER_DASHBOARD} replace />} />
            </Routes>
          </ProtectedRoute>
          </SchoolLanguageProvider>
        }
      />

      {/* Student Routes */}
      <Route
        path="/student/*"
        element={
          <SchoolLanguageProvider>
          <ProtectedRoute requiredRole={USER_ROLES.STUDENT} requireOnboarding>
            <Routes>
              <Route element={<StudentSectionLayout />}>
              <Route path="dashboard" element={<StudentDashboard />} />
              <Route path="courses" element={<StudentCourses />} />
              <Route path="grade/:grade/parts" element={<GradePartsList />} />
              <Route path="courses/:courseId" element={<StudentCoursePlayer />} />
              <Route
                path="profile/activity"
                element={<Navigate to={ROUTES.STUDENT_ACTIVITY} replace />}
              />
              <Route path="profile" element={<StudentProfile />} />
              <Route
                path="activity"
                element={
                  <StudentActivityGradeGate>
                    <StudentActivityPage />
                  </StudentActivityGradeGate>
                }
              />
              <Route path="conversation" element={<ConversationPage />} />
              <Route
                path="tamil-tutor"
                element={
                  <StudentSchoolFeatureGate featureKey="ai_tutor">
                    <TamilTutorPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="ai-tutor-live"
                element={
                  <StudentSchoolFeatureGate featureKey="ai_tutor">
                    <AiTutorLivePage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="communication-tutor"
                element={
                  <StudentSchoolFeatureGate featureKey="communication_tutor">
                    <CommunicationTutorPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="reading-comprehension"
                element={
                  <StudentSchoolFeatureGate featureKey="reading_comprehension">
                    <ReadingComprehensionAccessGate>
                      <ReadingComprehensionPage />
                    </ReadingComprehensionAccessGate>
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="demo/structured-reading/:pageId"
                element={
                  <StudentSchoolFeatureGate featureKey="reading_comprehension">
                    <ReadingComprehensionAccessGate>
                      <StructuredReadingDemoPage />
                    </ReadingComprehensionAccessGate>
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="writing-studio/*"
                element={<Navigate to={ROUTES.STUDENT_NARRATIVE_WRITING} replace />}
              />
              <Route
                path="narrative-writing"
                element={
                  <StudentSchoolFeatureGate featureKey="narrative_writing">
                    <NarrativeWritingLandingPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="narrative-writing/tutor"
                element={
                  <StudentSchoolFeatureGate featureKey="narrative_writing">
                    <NarrativeWritingTutorPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="narrative-writing/write"
                element={
                  <StudentSchoolFeatureGate featureKey="narrative_writing">
                    <NarrativeWritingWritePage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="narrative-writing/write/:draftId"
                element={
                  <StudentSchoolFeatureGate featureKey="narrative_writing">
                    <NarrativeWritingWritePage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="malay/expository-writing/:topicId"
                element={
                  <StudentSchoolFeatureGate featureKey="ai_tutor">
                    <ExpositoryWritingStudioPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="tamil/composition-writing"
                element={
                  <StudentSchoolFeatureGate featureKey="ai_tutor">
                    <TamilCompositionWritingStudioPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="tamil/keeladi-reading/:pageIndex"
                element={
                  <StudentSchoolFeatureGate featureKey="ai_tutor">
                    <KeeladiStructuredBookReaderPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="tamil/keeladi-reading"
                element={
                  <StudentSchoolFeatureGate featureKey="ai_tutor">
                    <KeeladiStructuredBookReaderPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="scenario-conversation"
                element={
                  <StudentSchoolFeatureGate featureKey="scenario_based_convo">
                    <ScenarioConversationPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="scenario-conversation/classroom"
                element={
                  <StudentSchoolFeatureGate featureKey="scenario_based_convo">
                    <ScenarioClassroomPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="communication-assignments"
                element={
                  <StudentSchoolFeatureGate featureKey="communication_tutor">
                    <Navigate to={ROUTES.STUDENT_ASSIGNMENTS_COMMUNICATION_TAB} replace />
                  </StudentSchoolFeatureGate>
                }
              />

              <Route path="games" element={<GamesPage />} />
              <Route path="phonics-lab" element={<TamilPhonicsFlashcards />} />
              <Route
                path="vocabulary"
                element={
                  <StudentSchoolFeatureGate featureKey="vocabulary">
                    <VocabularyHubPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="vocabulary/lesson/:lessonNo"
                element={
                  <StudentSchoolFeatureGate featureKey="vocabulary">
                    <VocabularyLessonPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="vocabulary/dictionary"
                element={
                  <StudentSchoolFeatureGate featureKey="vocabulary">
                    <VocabularyDictionaryPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="vocabulary/flashcards"
                element={
                  <StudentSchoolFeatureGate featureKey="vocabulary">
                    <VocabularyFlashcardsPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="vocabulary/flashcards/lesson/:lessonNo"
                element={
                  <StudentSchoolFeatureGate featureKey="vocabulary">
                    <RevisionFlashcardLessonPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route path="assignments" element={<StudentAssignmentsPage />} />
              <Route
                path="story-studio"
                element={
                  <StudentSchoolFeatureGate featureKey="story_studio">
                    <StoryStudioPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route path="ai-tutor-avatar" element={<AITutorAvatarPage />} />
              <Route
                path="picture-talk/:sceneId"
                element={
                  <StudentSchoolFeatureGate featureKey="picture_talk">
                    <PictureTalkScenePage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="picture-talk"
                element={
                  <StudentSchoolFeatureGate featureKey="picture_talk">
                    <PictureTalkListPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="writing/history"
                element={
                  <StudentSchoolFeatureGate featureKey="grammar">
                    <WritingHistoryPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="writing"
                element={
                  <StudentSchoolFeatureGate featureKey="grammar">
                    <WritingPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route path="library" element={<LibraryPage />} />
              <Route
                path="ibooks"
                element={
                  <StudentSchoolFeatureGate featureKey="ibook_reader_enabled">
                    <Suspense fallback={<RouteFallback />}>
                      <StudentIbookListPage />
                    </Suspense>
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="ibooks/interactive/:bookId"
                element={
                  <StudentSchoolFeatureGate featureKey="ibook_reader_enabled">
                    <Suspense fallback={<RouteFallback />}>
                      <StudentInteractiveBookReaderPage />
                    </Suspense>
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="ibooks/:bookId"
                element={
                  <StudentSchoolFeatureGate featureKey="ibook_reader_enabled">
                    <Suspense fallback={<RouteFallback />}>
                      <StudentIbookReaderPage />
                    </Suspense>
                  </StudentSchoolFeatureGate>
                }
              />
              <Route path="progress" element={<ProgressPage />} />
              <Route
                path="malay-learning-progress"
                element={
                  <StudentSchoolFeatureGate featureKey="ai_tutor">
                    <MalayLearningProgressPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route path="analytics/charts" element={<StudentAnalyticsChartsPage />} />
              <Route path="analytics" element={<StudentAnalyticsPage />} />
              <Route path="tutor-analytics" element={<TutorAnalyticsPage />} />
              <Route path="settings" element={<StudentSettingsPage />} />
              <Route
                path="support/*"
                element={
                  <SupportShellEmbeddedProvider>
                    <SupportRoutes />
                  </SupportShellEmbeddedProvider>
                }
              />
              <Route path="*" element={<Navigate to={ROUTES.STUDENT_DASHBOARD} replace />} />
              </Route>
            </Routes>
          </ProtectedRoute>
          </SchoolLanguageProvider>
        }
      />

      {/* Parent Routes */}
      <Route
        path="/parent/*"
        element={
          <SchoolLanguageProvider>
          <ProtectedRoute requiredRole={USER_ROLES.PARENT}>
            <Routes>
              <Route path="dashboard" element={<ParentDashboard />} />
              <Route path="link-child" element={<LinkChild />} />
              <Route path="children" element={<ChildrenList />} />
              <Route path="progress" element={<ParentCommunicationProgressPage />} />
              <Route path="support/*" element={<SupportRoutes />} />
              <Route path="*" element={<Navigate to={ROUTES.PARENT_DASHBOARD} replace />} />
            </Routes>
          </ProtectedRoute>
          </SchoolLanguageProvider>
        }
      />

      {/* Onboarding Routes */}
      <Route
        path="/onboarding/*"
        element={
          <ProtectedRoute>
            <Routes>
              <Route path="school-setup" element={<SchoolSetup />} />
              <Route path="teacher-profile" element={<TeacherProfilePage />} />
              <Route path="student-profile" element={<StudentProfilePage />} />
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* 404 */}
      <Route path="*" element={<Navigate to={ROUTES.LANDING} replace />} />
    </Routes>
    </Suspense>
  );
};

